import { layoutGiftcard, textWidth, ADVANCE, COLORS } from '../../shared/giftcard-layout.js';

/**
 * Paints the gift card image (spec staff-giftcards B3; direction contract in
 * `.impeccable/surfaces/js-components-giftcards-giftcardimage-js.md`) on a
 * canvas and returns it as a PNG blob. All positions come from
 * `layoutGiftcard`; this file only draws. Not unit-tested: jsdom has no
 * canvas. The wiring tests capture the model it receives, and the PNG is
 * checked in a real browser.
 */

export const LOGO_URL = '/img/panache-logo-negro.png';

const MONO = '"Courier Prime", "Courier New", monospace';
const STAMP_FONT = '"Outfit", system-ui, sans-serif';

/** @param {string} src @returns {Promise<HTMLImageElement | null>} */
function loadImage(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

async function loadFonts() {
    if (!document.fonts?.load) return;
    await Promise.all([
        document.fonts.load(`400 38px ${MONO}`),
        document.fonts.load(`700 38px ${MONO}`),
        document.fonts.load(`700 26px ${STAMP_FONT}`),
    ]).catch(() => {});
}

/**
 * Monospaced text with tracking: each glyph on the grid the layout measured.
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ text: string, x: number, y: number, size: number, bold: boolean, align: string, tracking: number, color: string }} t
 */
function drawText(ctx, t) {
    ctx.font = `${t.bold ? 700 : 400} ${t.size}px ${MONO}`;
    ctx.fillStyle = t.color;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    const width = textWidth(t.text, t.size, t.tracking);
    let x = t.align === 'right' ? t.x - width : t.align === 'center' ? t.x - width / 2 : t.x;
    const step = ADVANCE * t.size + t.tracking;
    for (const ch of t.text) {
        ctx.fillText(ch, x, t.y);
        x += step;
    }
}

/** The thermal ticket: straight top, zigzag tear at the bottom. */
function ticketPath(ctx, width, height) {
    const tooth = 34;
    const depth = 28;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(width, 0);
    ctx.lineTo(width, height);
    let up = true;
    for (let x = width - tooth / 2; x > 0; x -= tooth / 2) {
        ctx.lineTo(x, up ? height - depth : height);
        up = !up;
    }
    ctx.lineTo(0, height);
    ctx.closePath();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {ReturnType<typeof layoutGiftcard>['stamp']} stamp
 */
function drawStamp(ctx, stamp) {
    ctx.save();
    ctx.translate(stamp.cx, stamp.cy);
    ctx.rotate(stamp.rotation);
    ctx.beginPath();
    ctx.arc(0, 0, stamp.r, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(251, 250, 246, 0.6)';
    ctx.fill();
    ctx.lineWidth = 6;
    ctx.strokeStyle = COLORS.sello;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, stamp.r - 14, 0, Math.PI * 2);
    ctx.lineWidth = 2.5;
    ctx.stroke();

    const size = stamp.lines.length === 1 ? 28 : 24;
    ctx.font = `700 ${size}px ${STAMP_FONT}`;
    ctx.fillStyle = COLORS.sello;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const lineHeight = size * 1.2;
    const top = -((stamp.lines.length - 1) * lineHeight) / 2;
    stamp.lines.forEach((line, i) => {
        // Shrink a line that would touch the inner ring.
        const max = (stamp.r - 24) * 2 * Math.cos(Math.asin(Math.min(0.95, Math.abs(top + i * lineHeight) / (stamp.r - 24))));
        const w = ctx.measureText(line).width;
        ctx.save();
        if (w > max) {
            ctx.translate(0, top + i * lineHeight);
            ctx.scale(max / w, 1);
            ctx.fillText(line, 0, 0);
        } else {
            ctx.fillText(line, 0, top + i * lineHeight);
        }
        ctx.restore();
    });
    ctx.restore();
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {{ para: string, deParte: string | null, mensaje: string | null, monto: number, validaHasta: string }} model
 * @returns {Promise<Blob | null>} the PNG, or null when the browser cannot draw
 */
export async function renderGiftcard(canvas, model) {
    const plan = layoutGiftcard(model);
    canvas.width = plan.canvas.width;
    canvas.height = plan.canvas.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const [logo] = await Promise.all([loadImage(LOGO_URL), loadFonts()]);
    if (!logo) {
        // The card still draws (the text carries it), but a card without the
        // brand must not go unnoticed (review resilience-5).
        const err = new Error(`giftcard: no cargó el logo ${LOGO_URL}`);
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error(err.message);
    }

    ctx.fillStyle = COLORS.fondo;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const { ticket } = plan;
    ctx.save();
    ctx.translate(ticket.x + (ticket.width * ticket.scale) / 2, ticket.y + (ticket.height * ticket.scale) / 2);
    ctx.rotate(ticket.rotation);
    ctx.scale(ticket.scale, ticket.scale);
    ctx.translate(-ticket.width / 2, -ticket.height / 2);

    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
    ctx.shadowBlur = 50;
    ctx.shadowOffsetY = 26;
    ticketPath(ctx, ticket.width, ticket.height);
    ctx.fillStyle = COLORS.papel;
    ctx.fill();
    ctx.restore();

    if (logo) ctx.drawImage(logo, plan.logo.x, plan.logo.y, plan.logo.width, plan.logo.height);

    ctx.strokeStyle = COLORS.regla;
    ctx.lineCap = 'round';
    for (const rule of plan.rules) {
        ctx.beginPath();
        ctx.lineWidth = 4;
        ctx.setLineDash(rule.kind === 'dash' ? [14, 10] : [1, 9]);
        ctx.moveTo(rule.x1, rule.y);
        ctx.lineTo(rule.x2, rule.y);
        ctx.stroke();
    }
    ctx.setLineDash([]);

    for (const t of plan.texts) drawText(ctx, t);
    drawStamp(ctx, plan.stamp);
    ctx.restore();

    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'));
}
